import proj4 from 'proj4';

// Register coordinate system definitions once at module load
proj4.defs(
  'EPSG:27700',
  '+proj=tmerc +lat_0=49 +lon_0=-2 +k=0.9996012717 +x_0=400000 +y_0=-100000' +
    ' +ellps=airy +towgs84=446.448,-125.157,542.06,0.15,0.247,0.842,-20.489 +units=m +no_defs'
);
proj4.defs(
  'EPSG:29900',
  '+proj=tmerc +lat_0=53.5 +lon_0=-8 +k=1.000035 +x_0=200000 +y_0=250000' +
    ' +a=6377340.189 +b=6356034.447938534 +towgs84=482.5,-130.6,564.6,-1.042,-0.214,-0.631,8.15 +units=m +no_defs'
);

export interface ParsedCoordinate {
  lat: number;
  lng: number;
  displayName: string;
  format: string;
}

function isValidLatLng(lat: number, lng: number): boolean {
  return lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180;
}

/**
 * Map a grid letter (A–Z, skipping I) to a 0–24 value.
 * A=0 … H=7, J=8 … Z=24
 */
function letterVal(ch: string): number {
  const code = ch.toUpperCase().charCodeAt(0);
  return code > 73 ? code - 66 : code - 65; // 73 = 'I'
}

/**
 * Convert a British National Grid reference string to WGS84.
 * Accepts cleaned refs like "SU12345678" (even digit count 4–10).
 */
function bngToLatLng(ref: string): { lat: number; lng: number } | null {
  const clean = ref.toUpperCase().replace(/\s+/g, '');
  const m = clean.match(/^([A-HJ-Z]{2})(\d{4,10})$/);
  if (!m || m[2].length % 2 !== 0) return null;

  const l1 = letterVal(m[1][0]);
  const l2 = letterVal(m[1][1]);
  const digits = m[2];
  const half = digits.length / 2;
  const scale = Math.pow(10, 5 - half);

  // Each 500 km macro-square origin, offset to OSGB36 false origin
  const easting =
    (l1 % 5) * 500000 + (l2 % 5) * 100000 - 1000000 +
    parseInt(digits.slice(0, half), 10) * scale;
  const northing =
    (4 - Math.floor(l1 / 5)) * 500000 + (4 - Math.floor(l2 / 5)) * 100000 - 500000 +
    parseInt(digits.slice(half), 10) * scale;

  try {
    const [lng, lat] = proj4('EPSG:27700', 'EPSG:4326', [easting, northing]);
    return isValidLatLng(lat, lng) ? { lat, lng } : null;
  } catch {
    return null;
  }
}

/**
 * Convert an Irish National Grid reference string to WGS84.
 * Accepts cleaned refs like "O15993427" (single letter + even digit count 4–10).
 */
function irishGridToLatLng(ref: string): { lat: number; lng: number } | null {
  const clean = ref.toUpperCase().replace(/\s+/g, '');
  const m = clean.match(/^([A-HJ-Z])(\d{4,10})$/);
  if (!m || m[2].length % 2 !== 0) return null;

  const lv = letterVal(m[1]);
  const digits = m[2];
  const half = digits.length / 2;
  const scale = Math.pow(10, 5 - half);

  const easting = (lv % 5) * 100000 + parseInt(digits.slice(0, half), 10) * scale;
  const northing = (4 - Math.floor(lv / 5)) * 100000 + parseInt(digits.slice(half), 10) * scale;

  try {
    const [lng, lat] = proj4('EPSG:29900', 'EPSG:4326', [easting, northing]);
    return isValidLatLng(lat, lng) ? { lat, lng } : null;
  } catch {
    return null;
  }
}

function applyHemisphere(val: number, dir: string): number {
  const d = dir.toUpperCase();
  return d === 'S' || d === 'W' ? -Math.abs(val) : Math.abs(val);
}

function dmsToDecimal(deg: number, min: number, sec: number, dir: string): number {
  return applyHemisphere(deg + min / 60 + sec / 3600, dir);
}

function formatLatLng(lat: number, lng: number): string {
  const latDir = lat >= 0 ? 'N' : 'S';
  const lngDir = lng >= 0 ? 'E' : 'W';
  return `${Math.abs(lat).toFixed(5)}°${latDir}, ${Math.abs(lng).toFixed(5)}°${lngDir}`;
}

/**
 * Parse any supported coordinate format into WGS84 lat/lng.
 *
 * Supported formats:
 *   - Decimal Degrees:  "53.1234, -6.5678"  |  "53.1234 -6.5678"
 *   - DD with NSEW:     "53.1234°N 6.5678°W"  |  "N53.1234 W6.5678"
 *   - DMS:              "53°07'24\"N, 6°34'04\"W"
 *   - DDM:              "53°07.400'N, 6°34.070'W"
 *   - OS Grid (BNG):    "SU 123 456"  |  "SU12345678"
 *   - Irish Grid:       "O 159 342"   |  "O15993427"
 */
export function parseCoordinate(input: string): ParsedCoordinate | null {
  const s = input.trim();
  if (!s) return null;

  // ── DMS: 53°07'24"N, 6°34'04"W ──────────────────────────────────────────
  const dms = s.match(
    /^(\d{1,3})[°\s]\s*(\d{1,2})['\s]\s*(\d{1,2}(?:\.\d+)?)["\s]*([NS])\s*[,\s]\s*(\d{1,3})[°\s]\s*(\d{1,2})['\s]\s*(\d{1,2}(?:\.\d+)?)["\s]*([EW])$/i
  );
  if (dms) {
    const lat = dmsToDecimal(+dms[1], +dms[2], +dms[3], dms[4]);
    const lng = dmsToDecimal(+dms[5], +dms[6], +dms[7], dms[8]);
    if (isValidLatLng(lat, lng)) {
      return { lat, lng, displayName: formatLatLng(lat, lng), format: 'DMS' };
    }
  }

  // ── DDM: 53°07.400'N, 6°34.070'W ────────────────────────────────────────
  const ddm = s.match(
    /^(\d{1,3})[°\s]\s*(\d{1,2}(?:\.\d+)?)['\s]*([NS])\s*[,\s]\s*(\d{1,3})[°\s]\s*(\d{1,2}(?:\.\d+)?)['\s]*([EW])$/i
  );
  if (ddm) {
    const lat = dmsToDecimal(+ddm[1], +ddm[2], 0, ddm[3]);
    const lng = dmsToDecimal(+ddm[4], +ddm[5], 0, ddm[6]);
    if (isValidLatLng(lat, lng)) {
      return { lat, lng, displayName: formatLatLng(lat, lng), format: 'DDM' };
    }
  }

  // ── Decimal degrees with NSEW suffix: "53.1234°N 6.5678°W" ──────────────
  const ddNsew = s.match(
    /^(\d{1,2}(?:\.\d+)?)°?\s*([NS])\s*[,\s]\s*(\d{1,3}(?:\.\d+)?)°?\s*([EW])$/i
  );
  if (ddNsew) {
    const lat = applyHemisphere(+ddNsew[1], ddNsew[2]);
    const lng = applyHemisphere(+ddNsew[3], ddNsew[4]);
    if (isValidLatLng(lat, lng)) {
      return { lat, lng, displayName: formatLatLng(lat, lng), format: 'Decimal Degrees' };
    }
  }

  // ── Decimal degrees with NSEW prefix: "N53.1234 W6.5678" ─────────────────
  const ddNsewPrefix = s.match(
    /^([NS])\s*(\d{1,2}(?:\.\d+)?)°?\s*[,\s]\s*([EW])\s*(\d{1,3}(?:\.\d+)?)°?$/i
  );
  if (ddNsewPrefix) {
    const lat = applyHemisphere(+ddNsewPrefix[2], ddNsewPrefix[1]);
    const lng = applyHemisphere(+ddNsewPrefix[4], ddNsewPrefix[3]);
    if (isValidLatLng(lat, lng)) {
      return { lat, lng, displayName: formatLatLng(lat, lng), format: 'Decimal Degrees' };
    }
  }

  // ── Plain decimal degrees: "53.1234, -6.5678" or "53.1234 -6.5678" ──────
  const ddPlain = s.match(
    /^([-+]?\d{1,3}(?:\.\d+)?)\s*(?:,\s*|\s+)([-+]?\d{1,3}(?:\.\d+)?)$/
  );
  if (ddPlain) {
    const a = parseFloat(ddPlain[1]);
    const b = parseFloat(ddPlain[2]);
    // Resolve ambiguity: if one value exceeds ±90 it must be longitude
    let lat: number, lng: number;
    if (Math.abs(a) > 90 && Math.abs(b) <= 90) {
      lat = b; lng = a;
    } else if (Math.abs(b) > 90 && Math.abs(a) <= 90) {
      lat = a; lng = b;
    } else {
      // Both in lat range – assume conventional lat, lng order
      lat = a; lng = b;
    }
    if (isValidLatLng(lat, lng)) {
      return { lat, lng, displayName: formatLatLng(lat, lng), format: 'Decimal Degrees' };
    }
  }

  // ── OS Grid (BNG): "SU 123 456" or "SU12345678" ──────────────────────────
  const bngSpaced = s.match(/^([A-HJ-Z]{2})\s+(\d{3,5})\s+(\d{3,5})$/i);
  if (bngSpaced) {
    const digits = bngSpaced[2] + bngSpaced[3];
    if (digits.length % 2 === 0) {
      const pos = bngToLatLng(bngSpaced[1] + digits);
      if (pos) {
        return {
          ...pos,
          displayName: `${bngSpaced[1].toUpperCase()} ${bngSpaced[2]} ${bngSpaced[3]}`,
          format: 'OS Grid (BNG)',
        };
      }
    }
  }

  const bngCompact = s.match(/^([A-HJ-Z]{2}\d{4,10})$/i);
  if (bngCompact) {
    const pos = bngToLatLng(bngCompact[1]);
    if (pos) {
      return {
        ...pos,
        displayName: bngCompact[1].toUpperCase(),
        format: 'OS Grid (BNG)',
      };
    }
  }

  // ── Irish National Grid: "O 159 342" or "O15993427" ──────────────────────
  const igSpaced = s.match(/^([A-HJ-Z])\s+(\d{3,5})\s+(\d{3,5})$/i);
  if (igSpaced) {
    const digits = igSpaced[2] + igSpaced[3];
    if (digits.length % 2 === 0) {
      const pos = irishGridToLatLng(igSpaced[1] + digits);
      if (pos) {
        return {
          ...pos,
          displayName: `${igSpaced[1].toUpperCase()} ${igSpaced[2]} ${igSpaced[3]}`,
          format: 'Irish Grid',
        };
      }
    }
  }

  const igCompact = s.match(/^([A-HJ-Z]\d{4,10})$/i);
  if (igCompact) {
    const pos = irishGridToLatLng(igCompact[1]);
    if (pos) {
      return {
        ...pos,
        displayName: igCompact[1].toUpperCase(),
        format: 'Irish Grid',
      };
    }
  }

  return null;
}
